import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {useNavigate} from 'react-router';
import {
    listReportGaps,
    unlinkReportGap,
} from '../../../../../firebase/firestore/eei';
import {proposeIcrFromFintelGaps, resolveIcrNumber, resolveIcrRouteKey} from '../../../../../firebase/firestore/icr';
import {useSession} from '../../../../../firebase/UserProvider';
import SimilarityRelationList from '../../../../collection-requests/similarity/SimilarityRelationList';
import EntityTagsSection from '../../../../collection-requests/tags/EntityTagsSection';
import ManualLinkDialog from '../../../../collection-requests/similarity/ManualLinkDialog';
import {
    coveragePairBadges,
    resolveEntityCoverage,
} from '../../../../collection-requests/similarity/similarityLabels';
import {buildGapSimilarEdges} from '../gapSimilarEdges';

/**
 * Delta Gaps tab. Each persisted gap card shows: gap number + question,
 * tags, and a merged Similar list (proposed ICR/EEI, manual links, machine
 * suggestions). Gaps stay selectable for Propose ICR even after they were
 * proposed before.
 *
 * Optional focusGapId (gap id or gapNumber from ?gap=) scrolls that card
 * into view once gaps have loaded.
 */
export default function GapsToEeiView({
    reportId,
    enabled = true,
    focusGapId = null,
}) {
    const navigate = useNavigate();
    const {user, role, roles} = useSession();
    const canManage = role === 'collection_manager' || role === 'admin'
        || (roles || []).some((r) => r === 'collection_manager' || r === 'admin');

    const [gaps, setGaps] = useState([]);
    const [similarityMeta, setSimilarityMeta] = useState(null);
    const gapCardRefs = useRef(new Map());
    const focusedGapKeyRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(() => new Set());
    const [proposing, setProposing] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [title, setTitle] = useState('');
    const [question, setQuestion] = useState('');
    const [snack, setSnack] = useState({open: false, severity: 'success', msg: ''});
    // P7 manual similarity link (FULL/PARTIAL overlap) from a gap
    const [manualLink, setManualLink] = useState({open: false, gapId: null});

    const load = useCallback(async ({quiet = false} = {}) => {
        if (!reportId || !enabled) return;
        if (!quiet) setLoading(true);
        setError(null);
        try {
            const data = await listReportGaps(reportId);
            setGaps(data?.gaps || []);
            setSimilarityMeta(data?.similarity || null);
            if (!quiet) setSelected(new Set());
        } catch (e) {
            setError(e.message);
            if (!quiet) {
                setGaps([]);
                setSimilarityMeta(null);
            }
        } finally {
            if (!quiet) setLoading(false);
        }
    }, [reportId, enabled]);

    useEffect(() => {
        load();
    }, [load]);

    const similarityRunning = Boolean(similarityMeta?.running);

    useEffect(() => {
        if (!enabled || !reportId || !canManage || !similarityRunning) return undefined;
        const timer = setInterval(() => load({quiet: true}), 4000);
        return () => clearInterval(timer);
    }, [enabled, reportId, canManage, similarityRunning, load]);

    useEffect(() => {
        if (!focusGapId || loading || gaps.length === 0) return undefined;
        if (focusedGapKeyRef.current === focusGapId) return undefined;

        const match = gaps.find((gap) => (
            String(gap?.id) === String(focusGapId)
            || String(gap?.gapNumber || '') === String(focusGapId)
        ));
        if (!match) return undefined;

        const key = match.id || match.gapNumber || match.text;
        const el = gapCardRefs.current.get(String(key));
        if (!el) return undefined;

        focusedGapKeyRef.current = focusGapId;
        // Wait a frame so the Gaps tab scroll container has laid out.
        const raf = requestAnimationFrame(() => {
            el.scrollIntoView({behavior: 'smooth', block: 'center'});
        });
        return () => cancelAnimationFrame(raf);
    }, [focusGapId, gaps, loading]);

    const toggle = (key) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const gapKey = (gap) => gap.id || gap.text;

    const openConfirm = () => {
        if (selected.size === 0) return;
        setTitle('');
        setQuestion('');
        setConfirmOpen(true);
    };

    const selectedGapTexts = useMemo(() => {
        const texts = [];
        for (const gap of gaps) {
            if (selected.has(gapKey(gap))) texts.push(gap.text);
        }
        return texts;
    }, [gaps, selected]);

    const selectedAlreadyProposed = useMemo(
        () => gaps.filter((g) => selected.has(gapKey(g)) && (g.alreadyProposed || g.alreadyConverted)),
        [gaps, selected],
    );

    const manualLinkEdges = useMemo(() => {
        if (!manualLink.gapId) return [];
        const gap = gaps.find((g) => String(g.id) === String(manualLink.gapId));
        return gap ? buildGapSimilarEdges(gap) : [];
    }, [gaps, manualLink.gapId]);

    const propose = async () => {
        const gapTexts = selectedGapTexts;
        if (!gapTexts.length || proposing) return;
        setProposing(true);
        setError(null);
        try {
            const created = await proposeIcrFromFintelGaps({
                reportId,
                gapTexts,
                title: title.trim() || undefined,
                question: question.trim() || undefined,
                proposedBy: user?.uid || user?.email,
                owner: user?.uid || user?.email,
            });
            setConfirmOpen(false);
            await load({quiet: true});
            setSelected(new Set());

            const routeKey = resolveIcrRouteKey(created);
            const status = created.workflowStatus || 'Pending Approval';
            if (routeKey) {
                setSnack({
                    open: true,
                    severity: 'success',
                    msg: status === 'Active'
                        ? `ICR created: ${resolveIcrNumber(created) || routeKey}`
                        : 'ICR proposed — Pending Approval by a collection manager.',
                });
                setTimeout(() => navigate(`/icr/${encodeURIComponent(routeKey)}`), 800);
            } else {
                setSnack({
                    open: true,
                    severity: 'success',
                    msg: 'ICR proposed — Pending Approval by a collection manager.',
                });
            }
        } catch (e) {
            setError(e.message);
            setSnack({open: true, severity: 'error', msg: e.message || 'Failed to propose ICR'});
        } finally {
            setProposing(false);
        }
    };

    const onUnlink = async (gap, link) => {
        if (!gap?.id || !link?.id) return;
        try {
            await unlinkReportGap(reportId, gap.id, link.id);
            setSnack({open: true, severity: 'success', msg: 'Link removed'});
            await load({quiet: true});
        } catch (e) {
            setSnack({open: true, severity: 'error', msg: e.message || 'Unlink failed'});
        }
    };

    if (!enabled) return null;

    if (loading) {
        return (
            <Stack alignItems="center" py={4} spacing={1}>
                <CircularProgress size={28}/>
                <Typography variant="body2" color="text.secondary">
                    Loading gap questions…
                </Typography>
            </Stack>
        );
    }

    if (error && gaps.length === 0) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (gaps.length === 0) {
        return (
            <Box py={4}>
                <Typography color="text.secondary" align="center">
                    No explicit gap questions found in this report&apos;s Gaps section.
                </Typography>
            </Box>
        );
    }

    const selectedCount = selected.size;

    return (
        <Box sx={{pb: selectedCount > 0 ? 10 : 0}}>
            <Stack spacing={1.5}>
                {error && <Alert severity="warning" onClose={() => setError(null)}>{error}</Alert>}

                {similarityRunning && (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label="Similarity recomputing…" size="small" color="info" variant="outlined"/>
                    </Stack>
                )}

                {gaps.map((gap) => {
                    const key = gapKey(gap);
                    const checked = selected.has(key);
                    const similarEdges = buildGapSimilarEdges(gap);
                    const persisted = Boolean(gap.id);
                    const coverageBadges = coveragePairBadges(resolveEntityCoverage(gap), 'GAP');
                    const isFocused = Boolean(focusGapId) && (
                        String(gap?.id) === String(focusGapId)
                        || String(gap?.gapNumber || '') === String(focusGapId)
                    );
                    const refKey = String(gap.id || gap.gapNumber || key);

                    return (
                        <Paper
                            key={key}
                            ref={(node) => {
                                if (node) gapCardRefs.current.set(refKey, node);
                                else gapCardRefs.current.delete(refKey);
                            }}
                            variant="outlined"
                            data-gap-id={gap.id || undefined}
                            data-gap-number={gap.gapNumber || undefined}
                            sx={{
                                p: 1.5,
                                ...(isFocused ? {
                                    outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                                    outlineOffset: 2,
                                } : {}),
                            }}
                        >
                            <Stack spacing={1}>
                                <Stack spacing={0.5}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Checkbox
                                            size="small"
                                            checked={checked}
                                            disabled={proposing}
                                            onChange={() => toggle(key)}
                                            sx={{p: 0.5}}
                                        />
                                        {gap.gapNumber && (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{fontFamily: 'monospace', lineHeight: 1.5}}
                                            >
                                                {gap.gapNumber}
                                            </Typography>
                                        )}
                                    </Stack>
                                    <Typography variant="body2" sx={{pl: 5, pr: 0.5}}>
                                        {gap.text}
                                    </Typography>
                                    {coverageBadges.length > 0 && (
                                        <Stack
                                            direction="row"
                                            spacing={0.5}
                                            flexWrap="wrap"
                                            useFlexGap
                                            sx={{pl: 5}}
                                            data-testid="gap-coverage-chips"
                                        >
                                            {coverageBadges.map((b) => (
                                                <Chip
                                                    key={b.key}
                                                    label={b.label}
                                                    size="small"
                                                    color={b.tone === 'default' ? undefined : b.tone}
                                                    variant="outlined"
                                                />
                                            ))}
                                        </Stack>
                                    )}
                                </Stack>

                                {canManage && persisted && (
                                    <Box sx={{pl: 5}}>
                                        <EntityTagsSection
                                            entityType="GAP"
                                            entityId={gap.id}
                                            canEdit={canManage}
                                            enabled={canManage}
                                            dense
                                        />
                                    </Box>
                                )}

                                {canManage && persisted && (
                                    <Stack spacing={0.5} sx={{pl: 5}}>
                                        <Typography variant="caption" fontWeight={700} color="text.secondary">
                                            Similar
                                        </Typography>
                                        {similarEdges.length === 0 ? (
                                            <Typography variant="caption" color="text.secondary">
                                                {similarityRunning
                                                    ? 'Similarity recomputing in background…'
                                                    : 'No similar ICRs, EEIs, or gaps yet.'}
                                            </Typography>
                                        ) : (
                                            <Box sx={{minWidth: 0, width: '100%'}}>
                                                <SimilarityRelationList
                                                    edges={similarEdges}
                                                    dense
                                                    currentUserId={user?.uid || user?.email}
                                                    onEdgeReviewed={() => load({quiet: true})}
                                                    onUnlinkGapLink={(edge) => onUnlink(gap, {id: edge.id})}
                                                />
                                            </Box>
                                        )}
                                        <Box
                                            sx={{
                                                borderTop: '1px solid',
                                                borderColor: 'divider',
                                                pt: 0.75,
                                                mt: similarEdges.length > 0 ? 0 : 0.25,
                                            }}
                                        >
                                            <Button
                                                size="small"
                                                variant="text"
                                                onClick={() => setManualLink({open: true, gapId: gap.id})}
                                                sx={{alignSelf: 'flex-start', px: 0}}
                                            >
                                                Add Similar
                                            </Button>
                                        </Box>
                                    </Stack>
                                )}
                            </Stack>
                        </Paper>
                    );
                })}
            </Stack>

            {selectedCount > 0 && (
                <Box
                    sx={{
                        position: 'sticky',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        mt: 2,
                        py: 1.5,
                        px: 1,
                        bgcolor: 'background.paper',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        zIndex: 2,
                    }}
                >
                    <Button
                        fullWidth
                        variant="contained"
                        disabled={proposing}
                        onClick={openConfirm}
                    >
                        Propose ICR ({selectedCount} selected)
                    </Button>
                </Box>
            )}

            <Dialog open={confirmOpen} onClose={() => proposing ? null : setConfirmOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Propose ICR</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} pt={1}>
                        <Typography variant="body2" color="text.secondary">
                            {selectedCount} gap{selectedCount === 1 ? '' : 's'} will become EEIs under a new ICR
                            with status Pending Approval.
                        </Typography>
                        {selectedAlreadyProposed.length > 0 && (
                            <Alert severity="info" variant="outlined">
                                {selectedAlreadyProposed.length === 1
                                    ? 'One selected gap was already proposed'
                                    : `${selectedAlreadyProposed.length} selected gaps were already proposed`}
                                {' ('}
                                {selectedAlreadyProposed
                                    .map((g) => [g.gapNumber, g.icrNumber || g.eeiNumber]
                                        .filter(Boolean).join(' → ') || g.text)
                                    .join('; ')}
                                {'). Proposing again will create a new ICR proposal.'}
                            </Alert>
                        )}
                        <TextField
                            label="Title (optional)"
                            fullWidth
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            disabled={proposing}
                        />
                        <TextField
                            label="Question (optional)"
                            fullWidth
                            multiline
                            minRows={2}
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            disabled={proposing}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} disabled={proposing}>Cancel</Button>
                    <Button variant="contained" onClick={propose} disabled={proposing}>
                        {proposing ? 'Proposing…' : 'Propose ICR'}
                    </Button>
                </DialogActions>
            </Dialog>

            {manualLink.gapId && (
                <ManualLinkDialog
                    open={manualLink.open}
                    subject={{type: 'GAP', id: manualLink.gapId}}
                    existingEdges={manualLinkEdges}
                    onClose={() => setManualLink({open: false, gapId: null})}
                    onCreated={() => load({quiet: true})}
                />
            )}

            <Snackbar
                open={snack.open}
                autoHideDuration={3500}
                onClose={() => setSnack((p) => ({...p, open: false}))}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            >
                <Alert severity={snack.severity} variant="filled" sx={{width: '100%'}}>
                    {snack.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}
